import { formatDate } from '../../utils/dateFormat';
import { formatCurrency } from '../../utils/priceUtils';

/**
 * How the reports print a figure. Display only: the figures are the server's, summed per
 * currency, and nothing here converts one currency into another.
 *
 * The currency is written as its code ("TTD 1,234.50", "USD 50.00") rather than a symbol, so a
 * dollar sign never has to be guessed between TTD and USD in a report that shows both.
 */
export function formatReportMoney(value: number | null | undefined, currency: string): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '--';
  const text = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${currency || 'TTD'} ${text}`;
}

/**
 * A bill's money on the Accounts screens (the list and its cards, the bill, the supplier and the
 * payment vouchers): the money-label owner's shape, "TT$" or "US$", never a bare "$", and the
 * owner's "—" for a missing amount (D104.30).
 *
 * Each of those five pages carried its own `Intl.NumberFormat(undefined, { style: 'currency' })`,
 * whose symbol is the browser locale's: USD printed as "$2,000.00" on the Outstanding card with
 * nothing to tell it from TT$ (lane 5, V3d; BUSINESS_RULES#quotation-screen-money-labels, D96.16).
 * All this adds is what only a payable knows: no currency is TTD, the schema default
 * (`payableBalance.billCurrency`). Rounded to the cent, never ceiled: these are stored sums.
 */
export function formatBillMoney(value: number | null | undefined, currency?: string | null): string {
  return formatCurrency(value, currency || 'TTD', { ceil: false });
}

/** A report date, `yyyy-mm-dd` in, the house dd/mm/yyyy out. */
export function formatReportDay(value: string | null | undefined): string {
  return formatDate(value, '--');
}

/** "1 bill" / "3 bills". */
export function billCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'bill' : 'bills'}`;
}

/** A supplier saved with no name still gets a row, and a name to read. */
export function supplierLabel(name: string): string {
  return name.trim() === '' ? '(no supplier name)' : name;
}
